// useCollectorTier — pulls the user's PASS scan count via the SQL RPC and
// maps it to a tier name + fuzzy progress hint. The exact count and the
// per-tier thresholds NEVER leave this module — `pass_count` enters the
// pure helper, only `{ tier, hint }` leaves.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// ─── Tier domain ─────────────────────────────────────────────────────────────
// Thresholds aligned with badge_thresholds.collector_tier rows in the SQL.
// Drift between these and the SQL produces inconsistent UI vs awarded badges.
//
// Mapping: SPROUT (1) → PICKER (30) → RACK RUNNER (50) → ARCHIVIST (150)
//        → CONSERVATOR (500).

const TIERS = [
  { name: "Sprout",        min: 1,   nextMin: 30  as number | null },
  { name: "Picker",        min: 30,  nextMin: 50  as number | null },
  { name: "Rack Runner",   min: 50,  nextMin: 150 as number | null },
  { name: "Archivist",     min: 150, nextMin: 500 as number | null },
  { name: "Conservator",   min: 500, nextMin: null },
] as const;

export type TierName =
  | "Sprout"
  | "Picker"
  | "Rack Runner"
  | "Archivist"
  | "Conservator";

export type TierHint =
  | "Just getting started"
  | "Halfway to the next tier"
  | "Almost there!"
  | "Top of the closet";

export interface TierResult {
  tier: TierName | null;
  hint: TierHint | null;
}

/**
 * Pure mapping from PASS scan count → tier name + fuzzy hint. Exported for
 * unit testing only — production usage goes through `useCollectorTier`.
 *
 * Critically: the returned `hint` is one of four band labels — never a
 * percentage, never a count, never a remaining-to-go number. The intent is
 * a "closet-as-identity" tone, not a progress bar.
 */
export function computeTierAndHint(passCount: number): TierResult {
  if (passCount < 1) return { tier: null, hint: null };

  const tier = TIERS.find(
    (t) => passCount >= t.min && (t.nextMin === null || passCount < t.nextMin),
  );
  if (!tier) return { tier: null, hint: null };

  if (tier.nextMin === null) {
    return { tier: tier.name, hint: "Top of the closet" };
  }

  const progress = (passCount - tier.min) / (tier.nextMin - tier.min);
  let hint: TierHint;
  if (progress < 0.5) hint = "Just getting started";
  else if (progress < 0.85) hint = "Halfway to the next tier";
  else hint = "Almost there!";

  return { tier: tier.name, hint };
}

// ─── Hook ────────────────────────────────────────────────────────────────────

interface TierRpcRow {
  tier: string | null;
  pass_count: number;
}

/**
 * Live collector tier for a given user. Refetches when invalidated (e.g.
 * after a new PASS scan). Returns `{ tier, hint, isLoading }` — never the
 * raw count, which is consumed inside this hook.
 */
export function useCollectorTier(userId: string | null | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: ["collector-tier", userId],
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 min — tiers don't change every render
    queryFn: async (): Promise<TierResult> => {
      if (!userId) return { tier: null, hint: null };
      const { data, error } = await supabase.rpc("get_collector_tier", {
        target_user: userId,
      });
      if (error || !data || !Array.isArray(data) || data.length === 0) {
        return { tier: null, hint: null };
      }
      const row = data[0] as TierRpcRow;
      return computeTierAndHint(row.pass_count ?? 0);
    },
  });

  return {
    tier: data?.tier ?? null,
    hint: data?.hint ?? null,
    isLoading,
  };
}
